import { useState, useCallback } from 'react';
import { API_CONFIG, getAuthHeadersWithoutCredentials } from '../constants/api';
import toast from 'react-hot-toast';

export interface SoundworldProjectFormData {
    projectName: string;
    description: string;
    soundworld: string;
}

export const useSoundworldProjectCreation = () => {
    const [formData, setFormData] = useState<SoundworldProjectFormData>({
        projectName: '',
        description: '',
        soundworld: '',
    });

    const [isCreating, setIsCreating] = useState(false);

    const showError = useCallback((msg: string) => {
        toast.error(msg);
    }, []);

    const showSuccess = useCallback((msg: string) => {
        toast.success(msg);
    }, []);

    const createSoundworldProject = useCallback(async (projectName: string, description: string, soundworldName: string) => {
        if (!projectName.trim()) {
            showError("Please enter a project name.");
            return null;
        }

        if (!description.trim()) {
            showError("Please enter a project description.");
            return null;
        }

        if (!soundworldName) {
            showError("Please select a soundworld.");
            return null;
        }

        setIsCreating(true);
        try {
            // Create project using soundworld endpoint with name, description and soundworld as query parameters
            const queryParams = new URLSearchParams({
                name: projectName,
                description: description,
                soundworld: soundworldName
            });

            const res = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOUNDWORLD_NEW_PROJECT}?${queryParams.toString()}`,
                {
                    method: 'GET',
                    headers: getAuthHeadersWithoutCredentials(),
                }
            );

            if (!res.ok) {
                throw new Error("Failed to create project");
            }

            const newProject = await res.json();

            showSuccess("Project created successfully!");
            return newProject.project_id;
        } catch (err) {
            showError("Failed to create project.");
            return null;
        } finally {
            setIsCreating(false);
        }
    }, [showError, showSuccess]);

    return {
        formData,
        setFormData,
        isCreating,
        createSoundworldProject,
        showError,
        showSuccess,
    };
}; 